export type LocaleData = Record<string, any>

export type SupportedLocale = 'en' | 'ru' | 'uz' | 'zh'

export interface TOptions {
  count?: number
  [key: string]: any
}

const localeCache = new Map<SupportedLocale, LocaleData>()

export const loadLocaleMessages = async (locale: SupportedLocale): Promise<LocaleData> => {
  if (localeCache.has(locale)) {
    return localeCache.get(locale)!
  }

  try {
    let localeData: LocaleData

    switch (locale) {
      case 'en':
        localeData = (await import('@/locales/en/common.json')).default
        break
      case 'ru':
        localeData = (await import('@/locales/ru/common.json')).default
        break
      case 'uz':
        localeData = (await import('@/locales/uz/common.json')).default
        break
      case 'zh':
        localeData = (await import('@/locales/zh/common.json')).default
        break
      default:
        localeData = {}
    }

    localeCache.set(locale, localeData)
    return localeData
  } catch (error) {
    console.error(`Failed to load locale ${locale}:`, error)
    return {}
  }
}

export const getLocaleProps = async (locale: SupportedLocale) => {
  const messages = await loadLocaleMessages(locale)
  return {
    locale,
    messages,
  }
}

const getPluralRule = (locale: SupportedLocale, count: number): string => {
  switch (locale) {
    case 'en':
    case 'zh':
    case 'uz':
      return count === 1 ? 'one' : 'other'
    case 'ru':
      if (count !== Math.floor(count)) return 'other'

      const mod10 = count % 10
      const mod100 = count % 100
      if (mod10 === 1 && mod100 !== 11) return 'one'
      if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return 'few'
      return 'many'
    default:
      return 'other'
  }
}

const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined
  }, obj)
}

const interpolateString = (str: string, options: TOptions = {}): string => {
  return str.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return options[key] !== undefined ? String(options[key]) : match
  })
}

export const createTFunction = (locale: SupportedLocale, messages: LocaleData) => {
  return (key: string | TemplateStringsArray, options: TOptions = {}): string => {
    const keyStr = typeof key === 'string' ? key : key.join('')

    // Если есть count, сначала проверяем plural ключи
    if (options.count !== undefined) {
      const pluralRule = getPluralRule(locale, options.count)
      const pluralKey = `${keyStr}#${pluralRule}`
      const pluralValue = getNestedValue(messages, pluralKey)

      if (pluralValue !== undefined) {
        return interpolateString(String(pluralValue), options)
      }
    }

    // Затем ищем обычный ключ
    let value = getNestedValue(messages, keyStr)

    if (value === undefined) {
      console.warn(`Translation key "${keyStr}" not found for locale "${locale}"`)
      return keyStr
    }

    if (typeof value === 'string') {
      return interpolateString(value, options)
    }

    if (typeof value === 'object' && value !== null) {
      return interpolateString(String(value), options)
    }

    return interpolateString(String(value), options)
  }
}
